import React, { useEffect, useRef, useState } from "react";
import { Lock, Eye, EyeOff, Check, AlertTriangle } from "lucide-react";
import api from "../api";
import { useLang } from "../context/LanguageContext";

const BARS = ["bg-rose-500", "bg-rose-500", "bg-amber-500", "bg-[#16a34a]", "bg-[#16a34a]"];

// The meter calls the same checker the backend enforces, so nobody is told "strong" and then
// rejected on submit. A shop account is held to a higher bar than a shopper's.
export const PasswordField = ({ value, onChange, role = "shopper", email = "", name = "", businessName = "",
  placeholder, testid = "password-input", onReport, acceptWeak = false, onAcceptWeak }) => {
  const { t } = useLang();
  const [show, setShow] = useState(false);
  const [rep, setRep] = useState(null);
  const timer = useRef(null);

  useEffect(() => {
    clearTimeout(timer.current);
    if (!value) { setRep(null); onReport?.(null); return; }
    timer.current = setTimeout(() => {
      api.post("/auth/password-check", { password: value, role, email, name, businessName, acceptWeak })
        .then((r) => { setRep(r.data); onReport?.(r.data); })
        .catch(() => {});
    }, 350);
    return () => clearTimeout(timer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, role, email, name, businessName, acceptWeak]);

  const labels = [t("pw.veryWeak"), t("pw.weak"), t("pw.fair"), t("pw.strong"), t("pw.veryStrong")];

  return (
    <div>
      <div className="flex items-center gap-2.5 bg-secondary rounded-xl px-3.5 py-3">
        <Lock size={18} className="text-muted-foreground shrink-0" />
        <input type={show ? "text" : "password"} required value={value} onChange={onChange}
          data-testid={testid} placeholder={placeholder || t(role === "shopper" ? "pw.placeholder8" : "pw.placeholder12")} autoComplete="new-password"
          className="flex-1 bg-transparent outline-none text-sm text-foreground placeholder:text-muted-foreground" />
        <button type="button" onClick={() => setShow((s) => !s)} data-testid={`${testid}-toggle`}
          aria-label={show ? t("pw.hide") : t("pw.show")}
          className="text-muted-foreground hover:text-foreground transition-colors shrink-0">
          {show ? <EyeOff size={17} /> : <Eye size={17} />}
        </button>
      </div>

      {value && rep && (
        <div className="mt-2" data-testid={`${testid}-strength`}>
          <div className="flex gap-1">
            {[0, 1, 2, 3, 4].map((i) => (
              <span key={i} className={`h-1.5 flex-1 rounded-full transition-colors ${i <= rep.score ? (rep.ok ? BARS[rep.score] : "bg-rose-500") : "bg-border"}`} />
            ))}
          </div>
          <p className={`text-xs font-bold mt-1.5 ${rep.ok ? "text-[#16a34a]" : "text-rose-600"}`}
            data-testid={`${testid}-strength-label`}>
            {rep.ok ? <Check size={12} className="inline mb-0.5 mr-1" /> : <AlertTriangle size={12} className="inline mb-0.5 mr-1" />}
            {rep.ok ? `${labels[rep.score] || labels[0]}${rep.crackTime ? ` · ${t("pw.crackTime", { time: rep.crackTime })}` : ""}` : t("pw.needsWork")}
          </p>
          {!rep.ok && (rep.problems || []).map((p, i) => (
            <p key={i} className="text-[11px] text-muted-foreground mt-0.5">{p}</p>
          ))}
          {!rep.ok && (rep.hints || []).map((h, i) => (
            <p key={`h${i}`} className="text-[11px] text-muted-foreground/80 mt-0.5">{h}</p>
          ))}
          {rep.leaked && (
            <p className="text-[11px] font-bold text-rose-600 mt-1" data-testid={`${testid}-leaked`}>
              {t("pw.leaked", { n: (rep.breachCount || 0).toLocaleString() })}
            </p>
          )}
          {onAcceptWeak && (rep.canOverride || acceptWeak) && (
            <label className="mt-2 flex items-start gap-2 cursor-pointer" data-testid={`${testid}-accept-weak-label`}>
              <input type="checkbox" checked={acceptWeak} onChange={(e) => onAcceptWeak(e.target.checked)}
                data-testid={`${testid}-accept-weak`}
                className="mt-0.5 h-4 w-4 accent-[#16a34a] shrink-0 cursor-pointer" />
              <span className="text-[11px] text-muted-foreground">{t("pw.acceptWeak")}</span>
            </label>
          )}
          <p className="text-[11px] text-muted-foreground/80 mt-1" data-testid={`${testid}-floor-note`}>{t("pw.floorNote")}</p>
        </div>
      )}
    </div>
  );
};

export default PasswordField;
